import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { getCurrentUserOrgs } from "@services/api";
import { OrganizationWithRole } from "@/types/models";
import { RootState } from "../index";

// orgsSlice (Phase 3) holds the org list for the current user.
// Refetched on auth — not persisted. Active context lives on
// viewSlice.selectedOrgId so it survives refresh.

interface OrgsState {
  orgs: OrganizationWithRole[];
  isLoading: boolean;
  isError: boolean;
}

const initialState: OrgsState = {
  orgs: [],
  isLoading: false,
  isError: false,
};

export const loadCurrentUserOrgs = createAsyncThunk(
  "orgs/loadCurrentUserOrgs",
  async (_: void, thunkAPI) => {
    const result = await getCurrentUserOrgs();
    if (!result.success) {
      return thunkAPI.rejectWithValue(result.error.message);
    }
    return result.data;
  }
);

const orgsSlice = createSlice({
  name: "orgs",
  initialState,
  reducers: {
    clearOrgs: (state) => {
      state.orgs = [];
      state.isLoading = false;
      state.isError = false;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(loadCurrentUserOrgs.pending, (state) => {
        state.isLoading = true;
        state.isError = false;
      })
      .addCase(
        loadCurrentUserOrgs.fulfilled,
        (state, action: PayloadAction<OrganizationWithRole[]>) => {
          state.isLoading = false;
          state.orgs = Array.isArray(action.payload) ? action.payload : [];
        }
      )
      .addCase(loadCurrentUserOrgs.rejected, (state) => {
        state.isLoading = false;
        state.isError = true;
      });
  },
});

export const { clearOrgs } = orgsSlice.actions;

export const selectCurrentUserOrgs = (state: RootState): OrganizationWithRole[] =>
  Array.isArray(state.orgs?.orgs) ? state.orgs.orgs : [];
export const selectOrgsLoading = (state: RootState) => state.orgs.isLoading;

export default orgsSlice.reducer;
